import React from 'react';
import * as Sentry from '@sentry/react';
import { reportError } from '../utils/errorReporting'; // Use the centralized reporter

interface FallbackProps {
  error: Error;
  resetError: () => void;
  eventId?: string; // Pass Sentry event ID if available
}

// Simple Fallback Component (Customize as needed)
const ErrorFallback: React.FC<FallbackProps> = ({ error, resetError, eventId }) => (
  <div role="alert" className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
    <h2 className="font-bold">Something went wrong</h2>
    <p>{error.message || 'An unexpected error occurred.'}</p>
    {eventId && <p className="text-xs mt-2">Error ID: {eventId}</p>}
    <button
      onClick={resetError}
      className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
    >
      Try again
    </button>
  </div>
);

// Option 1: Using Sentry's HOC (Recommended)
export const AppErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError, eventId }) => {
      const normalizedError =
        error instanceof Error ? error : new Error(`An unknown error occurred: ${String(error)}`); // Create generic Error if needed

      return <ErrorFallback error={normalizedError} resetError={resetError} eventId={eventId} />;
    },
    onError: (error, componentStack, eventId) => {
      reportError(error, {
        componentStack,
        source: 'ErrorBoundary',
        sentryEventId: eventId,
      });
    },
    // beforeCapture: (scope) => { /* Add extra scope context if needed */ },
  }
);
